import askAboutObjectsApi from 'common/src/modules/askAboutObjects/askAboutObjects.api';
import {ToolExecuteParams, ToolResult} from 'common/src/modules/shared/copilot/copilot.types';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import toolResult from 'common/src/modules/shared/copilot/utils/creators/toolResult';
import {CLStore} from 'common/src/redux/store';
import {z} from 'zod';
import {CampaignSetupAgentToolName} from '../../../campaignSetupAgent.enums';
import {SearchObjectType} from 'common/src/modules/askAboutObjects/askAboutObjects.enums';

const querySchema = z.object({
  objectType: z
    .nativeEnum(SearchObjectType)
    .describe(
      'Placement questions are scoped to the current campaign version. Dsp questions are about DSP certification, macros, creative specs and data settings.',
    ),
  question: z.string().describe('Natural language question about the objects'),
});

const askAboutObjectsToolSchema = z.object({
  queries: z
    .array(querySchema)
    .min(1)
    .describe('List of object type and question pairs to ask about'),
});

export default function askAboutObjectsTool(store: CLStore) {
  async function execute({
    input: {queries},
  }: ToolExecuteParams<typeof askAboutObjectsToolSchema>): Promise<ToolResult> {
    const {campaign, versionId} = store.getState().campaignPage.campaignTree;

    const context = {
      campaignId: campaign?.id ?? null,
      campaignVersionId: versionId ?? null,
    };

    const responses = await Promise.all(
      queries.map(({objectType, question}) =>
        askAboutObjectsApi.askAboutObjects({objectType, question, context}),
      ),
    );

    const formattedResults = queries
      .map(({objectType, question}, index) => {
        const response = responses[index];

        return `[${objectType}] "${question}"\nAnswer: ${response.answer}`;
      })
      .join('\n\n---\n\n');

    return toolResult(formattedResults);
  }

  return tool({
    name: CampaignSetupAgentToolName.AskAboutObjects,
    displayName: 'Asking about objects',
    description:
      'Search account libraries for relevant objects or specific details. ' +
      'Object hierarchy: Libraries contain Feeds, Creatives, DSPs, and Campaigns. ' +
      'Feeds include Location Feeds, Product Feeds, and Content Feeds. ' +
      'Product Feeds contain Product Items, Product Sets (list of items), Top Items (list of items), and Category Top Items (list of categories, each with a list of items). ' +
      'Content Feeds contain Content Items and Content Sets (list of items). ' +
      'Location Feeds contain Location Items and Location Sets (list of items). ' +
      'Campaigns contain Placements, each belonging to a specific DSP. ' +
      'When querying by exact IDs, include all IDs in the question. Otherwise, describe the search criteria or the specific information needed concisely.',
    execute,
    inputSchema: askAboutObjectsToolSchema,
    isReadonly: true,
  });
}
